  import React, { useState } from "react";
import { useNavigate, Link } from "react-router";
import { VALIDATION_PATTERNS } from "../../constants/app";
import { sendForgotOtpReq, resetPasswordService } from "../../service/Auth.services";
import { showMainToast } from "utils/showToast";
import ShowToast from "components/ShowToast";
import APP_CONFIG from "../../constants/app";

/* ─────────────────────────────────────────────────────────
   Fintech Light Mode Theme — Forgot Password Screen
───────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  /* ── CSS Reset & Font Scoping ── */
  .mk-fp-root *, .mk-fp-root *::before, .mk-fp-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .mk-fp-root {
    min-height: 100vh;
    display: flex;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #f8fafc;
    color: #0f172a;
    position: relative;
    overflow-x: hidden;
  }

  /* ── Background Ambient Glows & Grid ── */
  .mk-bg-decor {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
  }

  .mk-bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(226, 232, 240, 0.6) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(226, 232, 240, 0.6) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
    -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
  }

  .mk-bg-glow-1 {
    position: absolute;
    width: 650px;
    height: 650px;
    top: -150px;
    left: -100px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-2 {
    position: absolute;
    width: 550px;
    height: 550px;
    bottom: -100px;
    right: 25%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.07) 0%, rgba(5, 150, 105, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-3 {
    position: absolute;
    width: 450px;
    height: 450px;
    top: 30%;
    right: -100px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  /* ── Layout Panels ── */
  .mk-main-container {
    display: flex;
    width: 100%;
    min-height: 100vh;
    position: relative;
    z-index: 1;
  }

  /* ── Left Hero Showcase Panel ── */
  .mk-hero-side {
    flex: 1.15;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 48px 56px;
    position: relative;
    z-index: 2;
  }

  .mk-brand-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 32px;
  }

  .mk-logo-img {
    height: 40px;
    max-width: 180px;
    object-fit: contain;
  }

  .mk-brand-fallback {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mk-brand-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #2563eb, #059669);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 800;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
  }

  .mk-brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
  }

  .mk-hero-content {
    max-width: 580px;
    margin: auto 0;
    padding: 20px 0;
  }

  .mk-tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }

  .mk-tag-pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    animation: mk-pulse-anim 2s infinite;
  }

  @keyframes mk-pulse-anim {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(0.9); }
  }

  .mk-hero-title {
    font-size: clamp(2rem, 3.2vw, 2.75rem);
    font-weight: 800;
    line-height: 1.18;
    letter-spacing: -0.03em;
    color: #0f172a;
    margin-bottom: 16px;
  }

  .mk-hero-title .mk-highlight-blue {
    color: #2563eb;
    background: linear-gradient(135deg, #1d4ed8, #2563eb);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-title .mk-highlight-green {
    color: #059669;
    background: linear-gradient(135deg, #059669, #10b981);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-desc {
    font-size: 15px;
    line-height: 1.65;
    color: #64748b;
    margin-bottom: 28px;
    font-weight: 450;
  }

  /* ── 2-Step Recovery Stepper ── */
  .mk-stepper-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 28px;
  }

  .mk-step-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
    transition: all 0.3s ease;
  }

  .mk-step-badge.active {
    background: #eff6ff;
    color: #2563eb;
    border: 1.5px solid #bfdbfe;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08);
  }

  .mk-step-badge.inactive {
    background: #ffffff;
    color: #94a3b8;
    border: 1px solid #e2e8f0;
  }

  .mk-step-badge.done {
    background: #ecfdf5;
    color: #059669;
    border: 1.5px solid #a7f3d0;
  }

  .mk-step-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 800;
    background: currentColor;
    color: #ffffff;
  }

  .mk-stepper-arrow {
    color: #cbd5e1;
    font-size: 16px;
    font-weight: 700;
  }

  /* ── 3 Feature Pillars: Payout, Wallet, Ledger ── */
  .mk-features-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 36px;
  }

  .mk-feature-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
    backdrop-filter: blur(10px);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .mk-feature-card:hover {
    transform: translateY(-2px);
    border-color: #cbd5e1;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  .mk-feature-icon-box {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .mk-icon-payout {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #dbeafe;
  }

  .mk-icon-wallet {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #d1fae5;
  }

  .mk-icon-ledger {
    background: #faf5ff;
    color: #7c3aed;
    border: 1px solid #f3e8ff;
  }

  .mk-feature-info {
    flex: 1;
    min-width: 0;
  }

  .mk-feature-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .mk-feature-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .mk-feature-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .mk-badge-blue { background: #dbeafe; color: #1e40af; }
  .mk-badge-green { background: #d1fae5; color: #065f46; }
  .mk-badge-purple { background: #ede9fe; color: #5b21b6; }

  .mk-feature-subtitle {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
  }

  /* ── Hero Footer Metrics ── */
  .mk-hero-footer {
    display: flex;
    align-items: center;
    gap: 28px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
  }

  .mk-stat-item {
    display: flex;
    flex-direction: column;
  }

  .mk-stat-number {
    font-family: 'JetBrains Mono', monospace;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .mk-stat-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ── Right Form Container Panel ── */
  .mk-form-side {
    width: 480px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 36px;
    position: relative;
    z-index: 2;
  }

  .mk-card-wrapper {
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 20px;
    padding: 36px 32px;
    box-shadow: 
      0 20px 40px -15px rgba(15, 23, 42, 0.07),
      0 1px 3px rgba(15, 23, 42, 0.04);
    position: relative;
  }

  .mk-form-header {
    margin-bottom: 24px;
  }

  .mk-card-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 4px 10px;
    border-radius: 6px;
    margin-bottom: 12px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .mk-form-heading {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin-bottom: 6px;
  }

  .mk-form-subtext {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }

  /* ── Progress Bar ── */
  .mk-step-progress-bar {
    height: 4px;
    background: #e2e8f0;
    border-radius: 4px;
    margin-bottom: 24px;
    overflow: hidden;
  }

  .mk-step-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #2563eb, #059669);
    border-radius: 4px;
    transition: width 0.4s ease;
  }

  /* ── Error Banner ── */
  .mk-alert-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: mk-shake 0.3s ease-in-out;
  }

  @keyframes mk-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }

  /* ── Form Inputs & Floating States ── */
  .mk-input-group {
    margin-bottom: 18px;
  }

  .mk-input-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }

  .mk-input-label {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.02em;
  }

  .mk-input-control {
    position: relative;
    display: flex;
    align-items: center;
  }

  .mk-control-icon {
    position: absolute;
    left: 14px;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: color 0.15s ease;
  }

  .mk-text-input {
    width: 100%;
    height: 48px;
    padding: 0 44px 0 42px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    outline: none;
    transition: all 0.2s ease;
  }

  .mk-text-input::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }

  .mk-text-input:focus {
    background: #ffffff;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
  }

  .mk-text-input:focus ~ .mk-control-icon {
    color: #2563eb;
  }

  .mk-otp-input {
    font-family: 'JetBrains Mono', monospace;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 12px;
    text-align: center;
    padding: 0 16px !important;
    color: #2563eb;
  }

  .mk-input-has-error {
    border-color: #ef4444 !important;
    background: #fffbfa !important;
  }

  .mk-input-has-error:focus {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }

  .mk-input-has-error ~ .mk-control-icon {
    color: #ef4444 !important;
  }

  .mk-toggle-pw-btn {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: color 0.15s ease, background-color 0.15s ease;
  }

  .mk-toggle-pw-btn:hover {
    color: #334155;
    background-color: #f1f5f9;
  }

  .mk-field-error {
    font-size: 11px;
    font-weight: 600;
    color: #ef4444;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .mk-resend-btn {
    background: none;
    border: none;
    font-size: 11.5px;
    font-weight: 600;
    color: #2563eb;
    cursor: pointer;
    padding: 0;
    font-family: inherit;
    transition: color 0.15s ease;
  }

  .mk-resend-btn:hover:not(:disabled) {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .mk-resend-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Submit Action Button ── */
  .mk-submit-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #0f172a, #1e293b);
    border: none;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
    transition: all 0.2s ease;
    margin-top: 10px;
  }

  .mk-submit-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e293b, #334155);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.24);
  }

  .mk-submit-btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  }

  .mk-submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  /* ── Back Navigation ── */
  .mk-back-row {
    margin-top: 24px;
    text-align: center;
  }

  .mk-back-link {
    font-size: 13px;
    font-weight: 600;
    color: #2563eb;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: color 0.15s ease;
  }

  .mk-back-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  /* ── Trust & Security Badges Footer ── */
  .mk-security-badges {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px dashed #e2e8f0;
  }

  .mk-security-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.02em;
  }

  .mk-security-icon {
    color: #059669;
    font-size: 12px;
  }

  /* ── Responsive Behavior ── */
  @media (max-width: 1024px) {
    .mk-hero-side {
      display: none;
    }
    .mk-form-side {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
      padding: 32px 20px;
    }
    .mk-card-wrapper {
      max-width: 100%;
      padding: 32px 24px;
    }
  }

  @media (max-width: 480px) {
    .mk-form-side {
      padding: 20px 14px;
    }
    .mk-card-wrapper {
      padding: 24px 18px;
      border-radius: 16px;
    }
    .mk-form-heading {
      font-size: 21px;
    }
  }
`;

/* ── Crisp Vector SVGs ── */
const PhoneIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LockIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const KeyIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-1.5 1.5L14 9l-3 3-2-2-4 4 2 2-4 4 2 2 4-4 2 2 3-3 3.5-3.5" />
    <circle cx="7.5" cy="7.5" r="3.5" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const PayoutIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const WalletIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
    <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
    <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
  </svg>
);

const LedgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="8" y1="7" x2="16" y2="7" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─────────────────────────────────────────────────────────
   Main ForGotPassword Component
───────────────────────────────────────────────────────── */
const ForGotPassword = () => {
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP + New Password
  const [formData, setFormData] = useState({
    email: "", // Phone
    otp: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (errors.general) setErrors((prev) => ({ ...prev, general: "" }));
  };

  // Step 1: Send OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!formData.email || !VALIDATION_PATTERNS.PHONE.test(formData.email)) {
      const msg = "Please enter a valid 10-digit mobile number";
      setErrors({ email: msg });
      ShowToast(msg, "error");
      return;
    }
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      const result = await sendForgotOtpReq(_formData);
      if (result.status) {
        const successMsg = result.message || "Verification code sent to your phone";
        ShowToast(successMsg, "success");
        setStep(2);
      } else {
        const errMsg = result.data?.error || result.data?.message || result.message || "Failed to send verification code";
        setErrors({ general: errMsg });
        ShowToast(errMsg, "error");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || error?.message || "Connection error. Please check your network and try again.";
      setErrors({ general: errMsg });
      ShowToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP in Step 2
  const handleResendOtp = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      const result = await sendForgotOtpReq(_formData);
      if (result.status) {
        const successMsg = result.message || `Verification code resent to ${formData.email}`;
        ShowToast(successMsg, "success");
      } else {
        const errMsg = result.data?.error || result.data?.message || result.message || "Failed to resend verification code";
        ShowToast(errMsg, "error");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || error?.message || "Error resending code. Please try again.";
      ShowToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final Submit (Verify OTP & Reset Password)
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.otp || !formData.otp.trim()) {
      newErrors.otp = "Please enter the 4-digit verification code";
    } else if (formData.otp.trim().length < 4) {
      newErrors.otp = "Please enter the complete 4-digit verification code";
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Clearly state first validation message in the toast
      const firstErrorMsg = newErrors.otp || newErrors.password || newErrors.confirmPassword;
      ShowToast(firstErrorMsg, "error");
      return;
    }

    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      _formData.append("otp", formData.otp.trim());
      const resetRes = await resetPasswordService(_formData);

      if (resetRes?.status === true || resetRes?.status === 1 || resetRes?.status === "success" || resetRes?.statusCode === 200) {
        const successMsg = resetRes?.message || resetRes?.data?.message || "Password reset successful! Please sign in with your new password.";
        ShowToast(successMsg, "success");
        navigate("/login", { state: { message: successMsg } });
      } else {
        const errMsg = resetRes?.data?.error || resetRes?.data?.message || resetRes?.message || resetRes?.error || "Password reset failed. Please check your OTP and try again.";
        setErrors({ general: errMsg });
        ShowToast(errMsg, "error");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || error?.message || "An unexpected error occurred. Please verify your OTP and try again.";
      setErrors({ general: errMsg });
      ShowToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const brandName = APP_CONFIG.ORGANISATION_BRAND || "Mokshiq";
  const brandLogo = APP_CONFIG.ORGANISATION_LOGO;

  return (
    <>
      <style>{styles}</style>

      <div className="mk-fp-root">
        {/* Background glow and decorative elements */}
        <div className="mk-bg-decor" aria-hidden="true">
          <div className="mk-bg-grid" />
          <div className="mk-bg-glow-1" />
          <div className="mk-bg-glow-2" />
          <div className="mk-bg-glow-3" />
        </div>

        <div className="mk-main-container">
          {/* ── Left Hero Showcase Panel ── */}
          <div className="mk-hero-side">
            <div className="mk-brand-header">
              {brandLogo ? (
                <img src={brandLogo} alt={brandName} className="mk-logo-img" />
              ) : (
                <div className="mk-brand-fallback">
                  <div className="mk-brand-icon">M</div>
                  <span className="mk-brand-name">{brandName}</span>
                </div>
              )}
            </div>

            <div className="mk-hero-content">
              <div className="mk-tag-pill">
                <span className="mk-tag-pulse" />
                <span>One Secure Dashboard • Account Recovery</span>
              </div>

              <h1 className="mk-hero-title">
                <span className="mk-highlight-blue">Payout</span>,{" "}
                <span className="mk-highlight-green">Wallet</span> &{" "}
                Ledger in One Secure Dashboard.
              </h1>

              <p className="mk-hero-desc">
                Reset your portal password securely with multi-factor OTP verification
                while ensuring your wallet balances and ledger logs stay completely protected.
              </p>

              {/* Stepper indicator */}
              <div className="mk-stepper-row">
                <div className={`mk-step-badge ${step === 1 ? "active" : "done"}`}>
                  <span className="mk-step-num">{step > 1 ? "✓" : "1"}</span>
                  <span>Phone Verification</span>
                </div>
                <span className="mk-stepper-arrow">→</span>
                <div className={`mk-step-badge ${step === 2 ? "active" : "inactive"}`}>
                  <span className="mk-step-num">2</span>
                  <span>Set New Password</span>
                </div>
              </div>

              {/* 3 Core Value Proposition Feature Cards */}
              <div className="mk-features-grid">
                {/* 1. Instant Payout Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-payout">
                    <PayoutIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Instant Payouts</span>
                      <span className="mk-feature-badge mk-badge-blue">24/7 Protected</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Zero unauthorized payouts — all settlement triggers are safeguarded with strict 2FA.
                    </p>
                  </div>
                </div>

                {/* 2. Multi-Wallet Control Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-wallet">
                    <WalletIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Multi-Wallet Security</span>
                      <span className="mk-feature-badge mk-badge-green">Locked Balances</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Main and BC Wallets remain securely encrypted throughout account recovery.
                    </p>
                  </div>
                </div>

                {/* 3. Audit-Ready Ledger Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-ledger">
                    <LedgerIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Audit-Ready Ledger</span>
                      <span className="mk-feature-badge mk-badge-purple">Audited Logs</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Every password reset action is timestamped and recorded in system security logs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Footer Stats */}
            <div className="mk-hero-footer">
              <div className="mk-stat-item">
                <span className="mk-stat-number">99.98%</span>
                <span className="mk-stat-label">Platform Uptime</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">₹450M+</span>
                <span className="mk-stat-label">Monthly Volume</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">256-Bit</span>
                <span className="mk-stat-label">Bank Encryption</span>
              </div>
            </div>
          </div>

          {/* ── Right Form Container Panel ── */}
          <div className="mk-form-side">
            <div className="mk-card-wrapper">
              <div className="mk-form-header">
                <div className="mk-card-pill">
                  <ShieldCheckIcon />
                  <span>Two-Factor Security Recovery</span>
                </div>
                <h2 className="mk-form-heading">
                  {step === 1 ? "Forgot Password?" : "Set New Password"}
                </h2>
                <p className="mk-form-subtext">
                  {step === 1
                    ? "Enter your registered mobile number to receive a verification code."
                    : `Enter the 4-digit code sent to ${formData.email} to complete reset.`}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mk-step-progress-bar">
                <div
                  className="mk-step-progress-fill"
                  style={{ width: step === 1 ? "50%" : "100%" }}
                />
              </div>

              {/* General Error Alert */}
              {errors.general && (
                <div className="mk-alert-error" role="alert">
                  <span>⚠️</span>
                  <span>{errors.general}</span>
                </div>
              )}

              <form onSubmit={step === 1 ? handleRequestOtp : handleResetSubmit} noValidate>
                {step === 1 ? (
                  /* ── STEP 1: PHONE INPUT ── */
                  <div className="mk-input-group">
                    <div className="mk-input-label-row">
                      <label className="mk-input-label" htmlFor="fp-phone">
                        Registered Mobile Number
                      </label>
                    </div>
                    <div className="mk-input-control">
                      <span className="mk-control-icon">
                        <PhoneIcon />
                      </span>
                      <input
                        id="fp-phone"
                        name="email"
                        type="tel"
                        className={`mk-text-input${errors.email ? " mk-input-has-error" : ""}`}
                        placeholder="Enter 10-digit mobile number"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading}
                        autoComplete="tel"
                        maxLength={10}
                      />
                    </div>
                    {errors.email && (
                      <div className="mk-field-error">
                        <span>⚠</span>
                        <span>{errors.email}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── STEP 2: OTP + PASSWORDS ── */
                  <>
                    {/* OTP Input */}
                    <div className="mk-input-group">
                      <div className="mk-input-label-row">
                        <label className="mk-input-label" htmlFor="fp-otp">
                          4-Digit Verification Code (OTP)
                        </label>
                        <button
                          type="button"
                          className="mk-resend-btn"
                          onClick={handleResendOtp}
                          disabled={loading}
                        >
                          Resend Code
                        </button>
                      </div>
                      <div className="mk-input-control">
                        <input
                          id="fp-otp"
                          name="otp"
                          type="text"
                          maxLength={4}
                          className={`mk-text-input mk-otp-input${errors.otp ? " mk-input-has-error" : ""}`}
                          placeholder="0 0 0 0"
                          value={formData.otp}
                          onChange={handleChange}
                          disabled={loading}
                          autoComplete="one-time-code"
                        />
                      </div>
                      {errors.otp && (
                        <div className="mk-field-error">
                          <span>⚠</span>
                          <span>{errors.otp}</span>
                        </div>
                      )}
                    </div>

                    {/* New Password */}
                    <div className="mk-input-group">
                      <div className="mk-input-label-row">
                        <label className="mk-input-label" htmlFor="fp-new-password">
                          New Password
                        </label>
                      </div>
                      <div className="mk-input-control">
                        <span className="mk-control-icon">
                          <LockIcon />
                        </span>
                        <input
                          id="fp-new-password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          className={`mk-text-input${errors.password ? " mk-input-has-error" : ""}`}
                          placeholder="Enter new password (min 6 chars)"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="mk-toggle-pw-btn"
                          onClick={() => setShowPassword((prev) => !prev)}
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                        </button>
                      </div>
                      {errors.password && (
                        <div className="mk-field-error">
                          <span>⚠</span>
                          <span>{errors.password}</span>
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="mk-input-group">
                      <div className="mk-input-label-row">
                        <label className="mk-input-label" htmlFor="fp-confirm-password">
                          Confirm New Password
                        </label>
                      </div>
                      <div className="mk-input-control">
                        <span className="mk-control-icon">
                          <KeyIcon />
                        </span>
                        <input
                          id="fp-confirm-password"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          className={`mk-text-input${errors.confirmPassword ? " mk-input-has-error" : ""}`}
                          placeholder="Re-enter new password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="mk-toggle-pw-btn"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          tabIndex={-1}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <div className="mk-field-error">
                          <span>⚠</span>
                          <span>{errors.confirmPassword}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Submit Action Button */}
                <button
                  type="submit"
                  className="mk-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1.1rem", height: "1.1rem", borderWidth: "2px" }}
                      />
                      <span>{step === 1 ? "Sending code…" : "Updating password…"}</span>
                    </>
                  ) : (
                    <>
                      <span>{step === 1 ? "Send Verification Code" : "Update Password & Sign In"}</span>
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>

              {/* Back Link */}
              <div className="mk-back-row">
                {step === 2 ? (
                  <button
                    type="button"
                    className="mk-back-link"
                    onClick={() => {
                      setStep(1);
                      setErrors({});
                    }}
                    disabled={loading}
                  >
                    ← Change Phone Number
                  </button>
                ) : (
                  <Link to="/login" className="mk-back-link">
                    ← Back to Sign In
                  </Link>
                )}
              </div>

              {/* Security Trust Badges */}
              <div className="mk-security-badges">
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>256-Bit SSL</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>OTP Protected</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>PCI DSS Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForGotPassword;